import React, { useState } from 'react';
import { TouchableOpacity, Text, StyleSheet, ActivityIndicator, Platform, Animated } from 'react-native';
import { useTheme } from '../context/ThemeContext';

const AnimatedButton = ({ 
  title, 
  disabled, 
  loading, 
  style, 
  textStyle, 
  indicatorColor,
  variant = 'primary', // primary, secondary, outline, ghost
  ...props 
}) => {
  const { palette } = useTheme();
  const [scaleAnim] = useState(new Animated.Value(1));
  const [isHovered, setIsHovered] = useState(false);

  const handlePressIn = () => {
    Animated.spring(scaleAnim, {
      toValue: 0.95,
      useNativeDriver: true,
      speed: 50,
    }).start();
  };

  const handlePressOut = () => {
    Animated.spring(scaleAnim, {
      toValue: 1,
      useNativeDriver: true,
      speed: 50,
    }).start();
  };

  const getButtonStyle = () => {
    const baseStyle = {
      backgroundColor: palette.primary,
      borderColor: 'transparent',
    };

    switch (variant) {
      case 'secondary':
        return {
          backgroundColor: palette.surface || 'rgba(255,255,255,0.1)',
          borderColor: palette.border || 'rgba(255,255,255,0.2)',
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          borderColor: palette.primary,
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          borderColor: 'transparent',
        };
      default:
        return baseStyle;
    }
  };

  const getTextColor = () => {
    switch (variant) {
      case 'secondary':
        return palette.text || 'white';
      case 'outline':
        return palette.primary;
      case 'ghost':
        return palette.text || 'white';
      default:
        return palette.contrastText || '#000';
    }
  };

  const buttonStyle = getButtonStyle();
  const textColor = getTextColor();

  const webHoverStyle = Platform.OS === 'web' && isHovered && !disabled && !loading
    ? {
        transform: [{ scale: 1.02 }],
        opacity: 0.9,
        ...Platform.select({
          web: {
            transition: 'all 0.2s ease',
          },
        }),
      }
    : {};

  return (
    <Animated.View
      style={{
        transform: [{ scale: scaleAnim }],
      }}
    >
      <TouchableOpacity
        style={[
          styles.button,
          buttonStyle,
          (disabled || loading) && styles.buttonDisabled,
          webHoverStyle,
          style,
        ]}
        disabled={disabled || loading}
        onPressIn={handlePressIn}
        onPressOut={handlePressOut}
        onMouseEnter={Platform.OS === 'web' ? () => setIsHovered(true) : undefined}
        onMouseLeave={Platform.OS === 'web' ? () => setIsHovered(false) : undefined}
        {...props}
      >
        {loading ? (
          <ActivityIndicator size="small" color={indicatorColor || textColor} />
        ) : (
          <Text style={[styles.text, textStyle, { color: textColor }]}>{title}</Text>
        )}
      </TouchableOpacity>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  button: {
    alignSelf: 'stretch',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      },
    }),
  },
  buttonDisabled: {
    opacity: 0.5,
    ...Platform.select({
      web: {
        cursor: 'not-allowed',
      },
    }),
  },
  text: {
    fontSize: 15,
    fontFamily: 'Archivo',
    fontWeight: '600',
    lineHeight: 20,
  },
});

export default AnimatedButton;

